import { isSafeRuntimeAttribute } from './safe-attributes';
import type { ScopedContextProvider } from './types';

type Attributes = Record<string, string | number | boolean>;
export const noopScopedContext: ScopedContextProvider = { setScopedAttributes: () => () => {} };

/** One conversation context at a time; replacement never resurrects an earlier owner. */
export function createScopedContext(sink: {
  add(attributes: Attributes): void | Promise<void>;
  remove(keys: string[]): void | Promise<void>;
}) {
  let active: { ownerId: string; keys: string[]; attributes: Attributes } | undefined;
  let queue = Promise.resolve();
  const enqueue = (write: () => void | Promise<void>) => {
    queue = queue.then(write).catch(() => {
      /* A vendor failure cannot own the member lifecycle. */
    });
  };
  return {
    setScopedAttributes(ownerId: string, attributes: Record<string, unknown>): () => void {
      if (!ownerId) return () => {};
      const safe: Attributes = {};
      for (const [key, value] of Object.entries(attributes)) {
        if (key.startsWith('runtime_') && isSafeRuntimeAttribute(key, value)) safe[key] = value;
      }
      const owner = { ownerId, keys: Object.keys(safe), attributes: safe };
      const obsolete = active?.keys.filter((key) => !Object.hasOwn(safe, key)) ?? [];
      active = owner;
      if (obsolete.length) enqueue(() => sink.remove(obsolete));
      if (owner.keys.length) enqueue(() => sink.add(safe));
      return () => {
        if (active !== owner) return;
        active = undefined;
        if (owner.keys.length) enqueue(() => sink.remove(owner.keys));
      };
    },
    refresh: () => {
      // Resolve ownership when the queued native view write executes.
      enqueue(() => (active ? sink.add(active.attributes) : undefined));
    },
    flush: () => queue,
  } satisfies ScopedContextProvider & { flush(): Promise<void>; refresh(): void };
}
